import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlogStore } from '../stores/blogStore';
import { Button } from '../components/common/Button';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Calendar,
  Tag as TagIcon,
  LayoutGrid,
  List,
  Wallpaper,
  Edit3,
} from 'lucide-react';
import { BlogLocale, BlogPost } from '../types/blog';

const BlogList: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { posts, fetchPosts, loading, hasMore, fetchMorePosts, filters, setFilters } =
    useBlogStore();

  const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== filters.search) {
        setFilters({ ...filters, search: searchQuery });
        fetchPosts({ filters: { ...filters, search: searchQuery }, offsetCount: 0 });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, filters, setFilters, fetchPosts]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleStatusFilter = (status: string) => {
    const newFilters = { ...filters, status };
    setFilters(newFilters);
    fetchPosts({ filters: newFilters, offsetCount: 0 });
    setShowFilters(false);
  };

  const handleLocaleFilter = (locale: '' | BlogLocale) => {
    const newFilters = { ...filters, locale };
    setFilters(newFilters);
    fetchPosts({ filters: newFilters, offsetCount: 0 });
    setShowFilters(false);
  };

  const getLocaleBadge = (locale: BlogLocale | undefined) => {
    const loc = locale ?? 'fr';
    return (
      <span
        className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
          loc === 'en'
            ? 'bg-sky-50 text-sky-800 border-sky-200'
            : 'bg-rose-50 text-rose-800 border-rose-200'
        }`}
      >
        {loc}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase tracking-wider border border-emerald-200">
            🚀 {t('status.published')}
          </span>
        );
      case 'scheduled':
        return (
          <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold uppercase tracking-wider border border-amber-200">
            🕒 {t('blogAdmin.scheduled')}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-[10px] font-bold uppercase tracking-wider border border-gray-200">
            📁 {t('status.draft')}
          </span>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pb-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-2">Blog</h1>
          <p className="text-gray-500 font-medium">{t('blogAdmin.manage_posts_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="transparent-with-border"
            onClick={() => navigate('/admin/blog/categories')}
            className="hidden md:flex items-center gap-2 border-gray-200 text-gray-600 hover:text-primary"
          >
            {t('navigation.categories')}
          </Button>
          <Button
            onClick={() => navigate('/admin/blog/new')}
            className="flex items-center gap-2 shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]"
          >
            <Plus size={20} />
            {t('blogAdmin.new_post')}
          </Button>
        </div>
      </div>

      {/* Filters & Tools */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2 w-full md:max-w-md shadow-sm focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
          <Search size={18} className="text-gray-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-transparent border-none focus:ring-0 text-sm w-full"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 border rounded-xl transition-colors ${filters.status || filters.locale ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 bg-white text-gray-500 hover:text-primary'}`}
            >
              <Filter size={18} />
            </button>
            {showFilters && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-1 animate-in fade-in zoom-in duration-200">
                <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  {t('blogAdmin.status')}
                </p>
                <button
                  onClick={() => handleStatusFilter('')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('blogAdmin.filter_all_status')}
                </button>
                <button
                  onClick={() => handleStatusFilter('published')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium text-emerald-600 transition-colors"
                >
                  🚀 {t('status.published')}
                </button>
                <button
                  onClick={() => handleStatusFilter('draft')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-600 transition-colors"
                >
                  📁 {t('status.draft')}
                </button>
                <button
                  onClick={() => handleStatusFilter('scheduled')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium text-amber-600 transition-colors"
                >
                  🕒 {t('blogAdmin.scheduled')}
                </button>
                <div className="my-1 border-t border-gray-100" />
                <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  {t('blogAdmin.language_section')}
                </p>
                <button
                  onClick={() => handleLocaleFilter('')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('blogAdmin.filter_all_locales')}
                </button>
                <button
                  onClick={() => handleLocaleFilter('fr')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('blogAdmin.filter_locale_fr')}
                </button>
                <button
                  onClick={() => handleLocaleFilter('en')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('blogAdmin.filter_locale_en')}
                </button>
              </div>
            )}
          </div>
          <div className="h-6 w-px bg-gray-200 mx-1" />
          <button
            onClick={() => setViewType('grid')}
            className={`p-2 border rounded-xl transition-all ${viewType === 'grid' ? 'border-primary/20 bg-primary/5 text-primary' : 'border-gray-200 bg-white text-gray-500 hover:text-gray-900'}`}
          >
            <LayoutGrid size={18} />
          </button>
          <button
            onClick={() => setViewType('list')}
            className={`p-2 border rounded-xl transition-all ${viewType === 'list' ? 'border-primary/20 bg-primary/5 text-primary' : 'border-gray-200 bg-white text-gray-500 hover:text-gray-900'}`}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Responsive Grid/List */}
      {viewType === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post: BlogPost) => (
            <div
              key={post.id}
              onClick={() => navigate(`/admin/blog/${post.id}`)}
              className="group bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col"
            >
              {/* Image Preview */}
              <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
                {post.featured_image_url ? (
                  <img
                    src={post.featured_image_url}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <Wallpaper size={48} />
                  </div>
                )}
                <div className="absolute top-4 right-4 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                  <button className="p-2 bg-white rounded-full shadow-lg text-gray-600 hover:text-primary">
                    <MoreHorizontal size={18} />
                  </button>
                </div>
                <div className="absolute top-4 left-4">{getLocaleBadge(post.locale)}</div>
                <div className="absolute bottom-4 left-4">{getStatusBadge(post.status)}</div>
              </div>

              {/* Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-primary mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}
                  </span>
                  {post.categories && post.categories.length > 0 && (
                    <span className="flex items-center gap-1 text-gray-400">
                      <TagIcon size={12} />
                      {post.categories[0].name}
                    </span>
                  )}
                </div>

                <h3 className="text-xl font-bold text-gray-900 line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-3 mb-6 flex-1">
                  {post.excerpt ||
                    (post.content
                      ? post.content.replace(/<[^>]*>?/gm, '').slice(0, 150) + '...'
                      : '')}
                </p>

                {/* Footer/Meta */}
                <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-400">/{post.slug}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center text-[10px] font-bold text-indigo-600 uppercase">
                      {post.author_name ? post.author_name.charAt(0) : 'P'}
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      {post.author_name}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[500px]">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-3">Title</th>
                  <th className="px-6 py-3">{t('blogAdmin.locale_column')}</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Author</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3">Published Date</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {posts.map((post: BlogPost) => (
                  <tr
                    key={post.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/blog/${post.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                          {post.featured_image_url ? (
                            <img
                              src={post.featured_image_url}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <Wallpaper size={20} />
                            </div>
                          )}
                        </div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {post.title}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getLocaleBadge(post.locale)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(post.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {post.author_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {post.categories?.[0]?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/admin/blog/${post.id}`);
                        }}
                        className="p-2 hover:bg-gray-200 rounded-lg text-gray-400 hover:text-primary transition-colors"
                      >
                        <Edit3 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {posts.length === 0 && !loading && (
        <div className="py-20 text-center space-y-4">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-300">
            <Wallpaper size={40} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{t('blogAdmin.no_posts_title')}</h3>
            <p className="text-sm text-gray-500">{t('blogAdmin.no_posts_hint')}</p>
          </div>
          <Button onClick={() => navigate('/admin/blog/new')} variant="transparent-with-border">
            {t('blogAdmin.new_post')}
          </Button>
        </div>
      )}

      {hasMore && (
        <div className="mt-12 flex justify-center">
          <Button
            variant="transparent-with-border"
            className="px-12 border-gray-200"
            onClick={fetchMorePosts}
            disabled={loading}
          >
            {loading ? t('loading') : t('loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BlogList;
